import React from 'react';
import { X, Filter } from 'lucide-react';
import { Input } from '../../../../../../components/ui/input';
import { MultiSelect, type Option } from '../../../../../../components/ui/multi-select';
import { Button } from '../../../../../../components/ui/button';
import type { MiscellaneousRegistryFilterState } from './hooks/useMiscellaneousRegistryFilters';

interface MiscellaneousRegistryFiltersProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  filters: MiscellaneousRegistryFilterState;
  onFilterChange: (key: keyof MiscellaneousRegistryFilterState) => (value: string[]) => void;
  uniqueMaterialTypes: string[];
  uniqueManufacturers: string[];
  uniqueLocations: string[];
  uniqueProductionTypes: string[];
  hasActiveFilters: boolean;
  filteredCount: number;
  onResetFilters: () => void;
}

export const MiscellaneousRegistryFilters: React.FC<MiscellaneousRegistryFiltersProps> = ({
  searchQuery,
  onSearchChange,
  filters,
  onFilterChange,
  uniqueMaterialTypes,
  uniqueManufacturers,
  uniqueLocations,
  uniqueProductionTypes,
  hasActiveFilters,
  filteredCount,
  onResetFilters,
}) => {
  const toOptions = (values: string[]): Option[] =>
    values.map((v) => ({ value: v, label: v }));

  return (
    <div className="mb-6 bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-4 h-8">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-500" />
          <span className="font-medium">Filters</span>
          {filteredCount > 0 && (
            <span className="text-sm text-gray-500 ml-2">
              ({filteredCount} miscellaneous {filteredCount === 1 ? 'item' : 'items'})
            </span>
          )}
        </div>
        <div className="h-8 flex items-center">
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onResetFilters}
              className="text-gray-500 hover:text-gray-700 transition-opacity duration-200"
            >
              <X className="h-4 w-4 mr-1" />
              Clear All
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-[180px] relative">
          <Input
            placeholder="Search by type, part, heat, lot, job, PO, qty, size, manufacturer, location, production, notes..."
            value={searchQuery}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onSearchChange(e.target.value)}
            className="w-full pr-8"
          />
        </div>
        <div className="w-[200px]">
          <MultiSelect
            options={toOptions(uniqueMaterialTypes)}
            selected={filters.materialType}
            onChange={onFilterChange('materialType')}
            placeholder="Material type"
            className="w-full"
            isSearchable
          />
        </div>
        <div className="w-[200px]">
          <MultiSelect
            options={toOptions(uniqueManufacturers)}
            selected={filters.manufacturer}
            onChange={onFilterChange('manufacturer')}
            placeholder="Manufacturer"
            className="w-full"
            isSearchable
          />
        </div>
        <div className="w-[200px]">
          <MultiSelect
            options={toOptions(uniqueLocations)}
            selected={filters.location}
            onChange={onFilterChange('location')}
            placeholder="Location"
            className="w-full"
            isSearchable
          />
        </div>
        <div className="w-[200px]">
          <MultiSelect
            options={toOptions(uniqueProductionTypes)}
            selected={filters.production}
            onChange={onFilterChange('production')}
            placeholder="Production"
            className="w-full"
            isSearchable
          />
        </div>
      </div>
    </div>
  );
};
